import { useState, useEffect, useCallback } from 'react';
import LoadingSpinner from './LoadingSpinner';
import { config } from '../config';

const GATEWAY_URL = config.gatewayUrl;

interface OidcProvider {
  id: string;
  provider_id: string;
  provider_type: string;
  label: string;
  client_id: string;
  tenant: string | null;
  issuer_url: string | null;
  auth_url: string | null;
  token_url: string | null;
  scopes: string[];
  mapper_claims: Record<string, string>;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

interface KratosConfig {
  kratos_yaml: string;
  mapper_files: Record<string, string>;
  instructions: string;
  provider_count: number;
  passport_callback_url: string;
}

const PROVIDER_TYPES = [
  { value: 'microsoft', label: 'Microsoft (Entra ID / Azure AD)' },
  { value: 'google', label: 'Google Workspace' },
  { value: 'github', label: 'GitHub' },
  { value: 'apple', label: 'Apple' },
  { value: 'gitlab', label: 'GitLab' },
  { value: 'slack', label: 'Slack' },
  { value: 'discord', label: 'Discord' },
  { value: 'generic', label: 'Generic OIDC' },
];

export default function AdminIdPManager() {
  const [providers, setProviders] = useState<OidcProvider[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [kratosConfig, setKratosConfig] = useState<KratosConfig | null>(null);

  const [formData, setFormData] = useState({
    provider_id: '',
    provider_type: 'microsoft',
    label: '',
    client_id: '',
    client_secret: '',
    tenant: '',
    issuer_url: '',
    scopes: '',
    enabled: true,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchProviders = useCallback(async () => {
    try {
      const res = await fetch(`${GATEWAY_URL}/v1/admin/oidc/providers`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`Failed to fetch providers: ${res.status}`);
      const data = await res.json();
      setProviders(data.providers || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load providers');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchProviders(); }, [fetchProviders]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        provider_id: formData.provider_id,
        provider_type: formData.provider_type,
        label: formData.label,
        client_id: formData.client_id,
        client_secret: formData.client_secret,
        enabled: formData.enabled,
      };
      if (formData.tenant) body.tenant = formData.tenant;
      if (formData.issuer_url) body.issuer_url = formData.issuer_url;
      if (formData.scopes) body.scopes = formData.scopes.split(',').map(s => s.trim()).filter(Boolean);

      const res = await fetch(`${GATEWAY_URL}/v1/admin/oidc/providers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: res.statusText }));
        throw new Error(err.detail || 'Failed to create provider');
      }
      setShowCreateModal(false);
      setFormData({
        provider_id: '', provider_type: 'microsoft', label: '',
        client_id: '', client_secret: '', tenant: '', issuer_url: '',
        scopes: '', enabled: true,
      });
      await fetchProviders();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create provider');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggle = async (providerId: string) => {
    try {
      const res = await fetch(`${GATEWAY_URL}/v1/admin/oidc/providers/${providerId}/toggle`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Toggle failed');
      await fetchProviders();
    } catch {
      setError('Failed to toggle provider');
    }
  };

  const handleDelete = async (providerId: string) => {
    if (!confirm(`Delete identity provider "${providerId}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${GATEWAY_URL}/v1/admin/oidc/providers/${providerId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Delete failed');
      await fetchProviders();
    } catch {
      setError('Failed to delete provider');
    }
  };

  const handleShowConfig = async () => {
    try {
      const res = await fetch(`${GATEWAY_URL}/v1/admin/oidc/kratos-config`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to generate config');
      const data = await res.json();
      setKratosConfig(data);
      setShowConfigModal(true);
    } catch {
      setError('Failed to generate Kratos config');
    }
  };

  const updateFormField = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'provider_type' && typeof value === 'string') {
      const type = PROVIDER_TYPES.find(t => t.value === value);
      if (type && !formData.label) {
        setFormData(prev => ({ ...prev, label: type.label.split(' (')[0], provider_id: value }));
      }
    }
  };

  if (isLoading) return <LoadingSpinner text="Loading identity providers..." />;

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg text-sm">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">Dismiss</button>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-content-primary">Identity Providers</h2>
          <p className="text-sm text-content-secondary">
            Configure SSO providers for federated login. After adding providers, generate and deploy the Kratos config.
          </p>
        </div>
        <div className="flex space-x-2">
          {providers.length > 0 && (
            <button onClick={handleShowConfig} className="btn-secondary text-sm">
              Generate Config
            </button>
          )}
          <button onClick={() => setShowCreateModal(true)} className="btn-primary text-sm">
            Add Provider
          </button>
        </div>
      </div>

      {providers.length === 0 ? (
        <div className="card text-center py-12">
          <svg className="w-12 h-12 mx-auto text-content-tertiary mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
          </svg>
          <p className="text-content-secondary mb-2">No identity providers configured</p>
          <p className="text-sm text-content-tertiary mb-4">Add an OIDC provider to enable SSO login buttons</p>
          <button onClick={() => setShowCreateModal(true)} className="btn-primary text-sm">
            Add First Provider
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-content-tertiary uppercase">Provider</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-content-tertiary uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-content-tertiary uppercase">Client ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-content-tertiary uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-content-tertiary uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {providers.map((p) => (
                <tr key={p.id}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-content-primary">{p.label}</div>
                    <div className="text-xs text-content-tertiary font-mono">{p.provider_id}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-content-secondary">{p.provider_type}</td>
                  <td className="px-4 py-3 text-sm text-content-secondary font-mono">
                    {p.client_id.length > 20 ? p.client_id.slice(0, 20) + '...' : p.client_id}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      p.enabled
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
                    }`}>
                      {p.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right space-x-2">
                    <button
                      onClick={() => handleToggle(p.provider_id)}
                      className="text-sm text-orbital-600 hover:text-orbital-800 dark:text-orbital-400"
                    >
                      {p.enabled ? 'Disable' : 'Enable'}
                    </button>
                    <button
                      onClick={() => handleDelete(p.provider_id)}
                      className="text-sm text-red-600 hover:text-red-800 dark:text-red-400"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Provider Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface-primary rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-content-primary mb-4">Add Identity Provider</h3>
              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-content-secondary mb-1">Provider Type</label>
                  <select
                    value={formData.provider_type}
                    onChange={(e) => updateFormField('provider_type', e.target.value)}
                    className="input w-full"
                  >
                    {PROVIDER_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-content-secondary mb-1">Provider ID</label>
                    <input
                      type="text"
                      value={formData.provider_id}
                      onChange={(e) => updateFormField('provider_id', e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
                      className="input w-full font-mono"
                      placeholder="microsoft"
                      required
                    />
                    <p className="text-xs text-content-tertiary mt-1">Slug used in config and URLs</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-content-secondary mb-1">Display Label</label>
                    <input
                      type="text"
                      value={formData.label}
                      onChange={(e) => updateFormField('label', e.target.value)}
                      className="input w-full"
                      placeholder="Microsoft"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-content-secondary mb-1">Client ID</label>
                  <input
                    type="text"
                    value={formData.client_id}
                    onChange={(e) => updateFormField('client_id', e.target.value)}
                    className="input w-full font-mono"
                    placeholder="Application (client) ID from the IdP"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-content-secondary mb-1">Client Secret</label>
                  <input
                    type="password"
                    value={formData.client_secret}
                    onChange={(e) => updateFormField('client_secret', e.target.value)}
                    className="input w-full font-mono"
                    placeholder="Client secret from the IdP"
                    required
                  />
                  <p className="text-xs text-content-tertiary mt-1">Encrypted at rest — never shown again</p>
                </div>

                {formData.provider_type === 'microsoft' && (
                  <div>
                    <label className="block text-sm font-medium text-content-secondary mb-1">
                      Tenant ID <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.tenant}
                      onChange={(e) => updateFormField('tenant', e.target.value)}
                      className="input w-full font-mono"
                      placeholder="Azure AD tenant ID or domain"
                      required
                    />
                  </div>
                )}

                {formData.provider_type === 'generic' && (
                  <div>
                    <label className="block text-sm font-medium text-content-secondary mb-1">
                      Issuer URL <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="url"
                      value={formData.issuer_url}
                      onChange={(e) => updateFormField('issuer_url', e.target.value)}
                      className="input w-full font-mono"
                      placeholder="https://idp.example.com"
                      required
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-content-secondary mb-1">Scopes (optional)</label>
                  <input
                    type="text"
                    value={formData.scopes}
                    onChange={(e) => updateFormField('scopes', e.target.value)}
                    className="input w-full"
                    placeholder="openid, profile, email (defaults applied per type)"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    id="provider-enabled"
                    type="checkbox"
                    checked={formData.enabled}
                    onChange={(e) => updateFormField('enabled', e.target.checked)}
                    className="h-4 w-4 rounded border-border text-orbital-600 focus:ring-orbital-500"
                  />
                  <label htmlFor="provider-enabled" className="ml-2 text-sm text-content-secondary">
                    Enable immediately
                  </label>
                </div>

                {error && (
                  <div className="text-sm text-red-600 dark:text-red-400">{error}</div>
                )}

                <div className="flex justify-end space-x-3 pt-2">
                  <button type="button" onClick={() => setShowCreateModal(false)} className="btn-secondary">
                    Cancel
                  </button>
                  <button type="submit" disabled={isSubmitting} className="btn-primary">
                    {isSubmitting ? <LoadingSpinner size="sm" /> : 'Add Provider'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Config Output Modal */}
      {showConfigModal && kratosConfig && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface-primary rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-content-primary mb-2">Kratos OIDC Configuration</h3>
              <p className="text-sm text-content-secondary mb-4">
                {kratosConfig.provider_count} provider(s) enabled. Copy this config and deploy to Kratos.
              </p>

              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm font-medium text-content-secondary">Callback URL (register in each IdP)</label>
                    <button
                      onClick={() => navigator.clipboard.writeText(kratosConfig.passport_callback_url)}
                      className="text-xs text-orbital-600 hover:text-orbital-800"
                    >
                      Copy
                    </button>
                  </div>
                  <code className="block bg-surface-secondary rounded p-2 text-sm font-mono text-content-primary break-all">
                    {kratosConfig.passport_callback_url}
                  </code>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm font-medium text-content-secondary">
                      Kratos Config (selfservice.methods.oidc.config)
                    </label>
                    <button
                      onClick={() => navigator.clipboard.writeText(kratosConfig.kratos_yaml)}
                      className="text-xs text-orbital-600 hover:text-orbital-800"
                    >
                      Copy
                    </button>
                  </div>
                  <pre className="bg-surface-secondary rounded p-3 text-sm font-mono text-content-primary overflow-x-auto max-h-60">
                    {kratosConfig.kratos_yaml}
                  </pre>
                </div>

                {Object.entries(kratosConfig.mapper_files).map(([name, content]) => (
                  <div key={name}>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-sm font-medium text-content-secondary">
                        Mapper: {name}
                      </label>
                      <button
                        onClick={() => navigator.clipboard.writeText(content)}
                        className="text-xs text-orbital-600 hover:text-orbital-800"
                      >
                        Copy
                      </button>
                    </div>
                    <pre className="bg-surface-secondary rounded p-3 text-sm font-mono text-content-primary overflow-x-auto max-h-40">
                      {content}
                    </pre>
                  </div>
                ))}

                <div className="bg-blue-50 dark:bg-blue-900/20 rounded p-3">
                  <p className="text-sm text-blue-800 dark:text-blue-200 font-medium mb-1">Deployment Steps</p>
                  <pre className="text-xs text-blue-700 dark:text-blue-300 whitespace-pre-wrap">{kratosConfig.instructions}</pre>
                </div>
              </div>

              <div className="flex justify-end mt-4">
                <button onClick={() => setShowConfigModal(false)} className="btn-primary">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
